/**
 * useContractsFeatureAccess — the access SEAM for the Contracts feature (CB-8.1, FR-70; RBAC-6.2).
 *
 * Single source of truth for "may the current user use Contracts at all?". Built ON TOP of the
 * existing `PermissionContext` and the account registry (`useAccountFeatures`). Pure decision logic
 * lives in `@/lib/contractAccess`; this hook only wires the live inputs to it.
 *
 * RBAC-6.2: the answer is the account's `contracts` module plus `canReadContracts`, the same
 * permissions `contracts_select` asks. The role list it used to read from tenant_settings is gone.
 *
 * Contract (stable — consumed by the project Contracts tab, the Contracts page, the
 * ContractHistoryPanel action bar, and the direct-URL no-access guard):
 *   output — { hasAccess, loading, accountEnabled }
 *
 * While `loading` is true, callers should defer the access decision (avoid flashing "no access").
 */
import { usePermissions } from '@/contexts/PermissionContext';
import { useAccountFeatures } from '@/hooks/useAccountFeatures';
import { canReadContracts, computeContractsFeatureAccess } from '@/lib/contractAccess';

export interface ContractsFeatureAccess {
  hasAccess: boolean;
  loading: boolean;
  /**
   * RBAC-6.1. Whether the ACCOUNT has Contracts switched on. Lets the lock screen say which of the two
   * reasons applies: an admin can fix a missing permission, only a Joules superadmin can fix this one.
   */
  accountEnabled: boolean;
}

export function useContractsFeatureAccess(): ContractsFeatureAccess {
  const { hasPermission, loading: permsLoading } = usePermissions();
  // RBAC-6.1 / FR-19: the account switch in the feature registry comes before the permission.
  const { isOn, loading: featuresLoading } = useAccountFeatures();
  const loading = permsLoading || featuresLoading;

  const hasAccess = computeContractsFeatureAccess({
    accountEnabled: isOn('contracts'),
    canRead: canReadContracts(hasPermission),
    isSuperadmin: hasPermission('system.override'),
  });
  return { hasAccess, loading, accountEnabled: !featuresLoading && isOn('contracts') };
}
